import * as B from '@blueprintjs/core';
import React from 'react';
import {Link} from 'react-router-dom';

import {
  CartItemGroupCard,
  formatBilledAcresLabel,
  sceneCountLabel,
  useCollapsedGroups,
} from 'app/modules/Cart';
import type {CartGroupBy} from 'app/modules/Cart';
import {formatCost} from 'app/utils/costUtils';

import type {OrderedGroupBy} from './grouping';
import {groupOrderedLines, orderedPropertyCount} from './grouping';
import OrderedSceneRow from './OrderedSceneRow';
import cs from './styles.module.scss';
import type {OrderedLine} from './types';

const propertyCountLabel = (count: number): string =>
  count === 1 ? '1 property' : `${count} properties`;

/** A card checkout's Stripe invoice, one per charge. A pay-now cart is charged as a single checkout. */
export interface CheckoutInvoiceLink {
  key: string;
  /** The portfolios the invoice covers. */
  label: string;
  hostedInvoiceUrl: string;
}

export interface CheckoutSuccessProps {
  lines: OrderedLine[];
  /** What the buyer was charged, card tax included. */
  totalCents: number;
  /** The tax in `totalCents`, which the scene prices below don't carry. */
  taxCents: number;
  totalBilledAcres: number;
  /** Prepaid credit the order drew, before tax; zero says nothing about credit at all. */
  prepaidCents: number;
  /** The organization's credit once the order drew on it; null until that's known. */
  creditLeftCents: number | null;
  /** Empty for a contract-billed checkout, which is printed as a receipt instead. */
  invoices: CheckoutInvoiceLink[];
  /** Where the delivery note says the scenes will be announced. */
  userEmail: string;
  groupBy: OrderedGroupBy;
  /** False hides the Property option: no property has two scenes, so it would group nothing. */
  propertyGroupingIsAvailable: boolean;
  onGroupByChange: (groupBy: CartGroupBy) => void;
  browsePropertiesUrl: string;
  billingUrl: string;
  /** Undefined for a line that couldn't be paired back to the cart row it was ordered from. */
  propertyLinkFor: (line: OrderedLine) => string | undefined;
}

/** What an order came to, once every scene in the cart went through. */
export const CheckoutSuccess: React.FC<CheckoutSuccessProps> = ({
  lines,
  totalCents,
  taxCents,
  totalBilledAcres,
  prepaidCents,
  creditLeftCents,
  invoices,
  userEmail,
  groupBy,
  propertyGroupingIsAvailable,
  onGroupByChange,
  browsePropertiesUrl,
  billingUrl,
  propertyLinkFor,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const {isCollapsed, toggle} = useCollapsedGroups(groupBy);

  const propertyCount = orderedPropertyCount(lines);
  const groups = React.useMemo(() => groupOrderedLines(lines, groupBy), [lines, groupBy]);

  const groupByOptions: {value: OrderedGroupBy; label: string}[] = [
    ...(propertyGroupingIsAvailable ? [{value: 'property' as const, label: 'Property'}] : []),
    {value: 'portfolio', label: 'Portfolio'},
  ];

  return (
    <div className={cs.cartPage} ref={rootRef}>
      <div className={cs.confirmation}>
        <header className={cs.pageHeader}>
          <div>
            <h2 className={cs.confirmationTitle}>
              <B.Icon icon="tick-circle" intent={B.Intent.SUCCESS} size={24} />
              Order placed
            </h2>
            <div>
              We’re preparing {sceneCountLabel(lines.length)} across{' '}
              {propertyCountLabel(propertyCount)}.
            </div>
          </div>
        </header>

        <div className={cs.orderSummaryBar}>
          <span>
            {[
              sceneCountLabel(lines.length),
              propertyCountLabel(propertyCount),
              formatBilledAcresLabel(totalBilledAcres),
            ].join(' · ')}
          </span>
          <div className={cs.orderSummaryTotal}>
            <span className={cs.pageHeaderMeta}>Total</span>
            <span className={cs.orderSummaryTotalValue}>{formatCost(totalCents)}</span>
          </div>
          {/* Beside the total rather than in the footer, so a long receipt doesn't hide it. */}
          <div className={cs.actions}>
            <ReceiptAction
              invoices={invoices}
              onPrint={() => rootRef.current?.ownerDocument?.defaultView?.print()}
            />
          </div>
        </div>

        <div className={cs.deliveryNote}>
          <p>
            We’ll email <strong>{userEmail}</strong> as each scene lands in Lens. Most complete
            within 24 hours.
          </p>
          <p>These purchases can’t be refunded.</p>
        </div>

        <section className={cs.breakdown} aria-labelledby="checkout-success-breakdown-heading">
          <div className={cs.breakdownHeader}>
            <h3 id="checkout-success-breakdown-heading">Scene breakdown</h3>
            {groupByOptions.length > 1 && (
              <div className={cs.breakdownControl}>
                <span className={cs.pageHeaderMeta}>Group by</span>
                <B.SegmentedControl
                  value={groupBy}
                  onValueChange={(value) => onGroupByChange(value as CartGroupBy)}
                  options={groupByOptions}
                />
              </div>
            )}
          </div>

          {groups.map((group) => (
            <CartItemGroupCard
              key={group.key}
              group={group}
              layout="receipt"
              isCollapsed={isCollapsed(group)}
              onToggle={() => toggle(group)}
              titleUrl={groupBy === 'property' ? propertyLinkFor(group.items[0]) : undefined}
            >
              {group.items.map((line) => (
                <OrderedSceneRow
                  key={line.ordered.purchaseId}
                  line={line}
                  showProperty={groupBy === 'portfolio'}
                />
              ))}
            </CartItemGroupCard>
          ))}
        </section>

        <footer className={cs.pageFooter}>
          <div className={cs.pageTotals}>
            <div className={cs.pageTotal}>
              <span>Total charged</span>
              <strong className={cs.pageTotalValue}>{formatCost(totalCents)}</strong>
              {taxCents > 0 && (
                <span className={cs.pageHeaderMeta}>Includes {formatCost(taxCents)} tax</span>
              )}
            </div>
            {prepaidCents > 0 && (
              <dl className={cs.creditSummary}>
                <div>
                  <dt>Prepaid credit used</dt>
                  <dd>{formatCost(prepaidCents)}</dd>
                </div>
                {creditLeftCents !== null && (
                  <div>
                    <dt>Prepaid credit remaining</dt>
                    <dd>{formatCost(creditLeftCents)}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>

          <div className={cs.footerActions}>
            <Link to={billingUrl} className={cs.billingLink}>
              View all purchases in Billing
            </Link>
            <Link to={browsePropertiesUrl}>
              <B.Button intent={B.Intent.PRIMARY}>Back to properties</B.Button>
            </Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

const ReceiptAction: React.FC<{invoices: CheckoutInvoiceLink[]; onPrint: () => void}> = ({
  invoices,
  onPrint,
}) => {
  if (invoices.length === 0) {
    return (
      <B.Button outlined icon="print" onClick={onPrint}>
        Print receipt
      </B.Button>
    );
  }

  if (invoices.length === 1) {
    return (
      <B.AnchorButton
        outlined
        icon="document-open"
        href={invoices[0].hostedInvoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        View invoice
      </B.AnchorButton>
    );
  }

  return (
    <B.Popover
      placement="bottom-end"
      content={
        <B.Menu>
          {invoices.map((invoice) => (
            <B.MenuItem
              key={invoice.key}
              icon="document-open"
              text={invoice.label}
              href={invoice.hostedInvoiceUrl}
              target="_blank"
            />
          ))}
        </B.Menu>
      }
    >
      <B.Button outlined icon="document-open" rightIcon="caret-down">
        View invoices
      </B.Button>
    </B.Popover>
  );
};

export default CheckoutSuccess;
